import { Engine } from "@babylonjs/core/Engines/engine";
import { Scene } from "@babylonjs/core/scene";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Viewport } from "@babylonjs/core/Maths/math.viewport";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import type { ThemeId, TrackSpec } from "../config/tracks";
import { crossOffset, curlOf, roadFrames } from "../track/Track";

/**
 * Course-select previews, F-Zero-GX style: each course as a small glowing 3D
 * model turning on a pedestal, drawn live so it spins smoothly at the
 * display's frame rate. Every course renders in one pass of one small
 * offscreen WebGL canvas, a tile each (each tile's camera sees only its own
 * course), and each card's canvas copies its tile. That one extra context
 * lives only while the course select is on screen.
 */
const W = 240;
const H = 150;
/** Seconds per turn of the pedestal. */
const SPIN_SECONDS = 3.6;
/** Frames with no preview on screen before the context is let go. */
const IDLE_FRAMES = 30;
/** Strips across a curled road in the model. */
const MINI_STRIPS = 12;
/** One camera layer per course, and Babylon has 28 to give. */
const MAX_COURSES = 28;

const ACCENT: Record<ThemeId, string> = {
  neon: "#3aa6e6",
  ocean: "#7fe8ff",
  port: "#ffb347",
  city: "#ff4fd8",
  casino: "#ffd23f",
  desert: "#ffcf7a",
  storm: "#9fb4ff",
  sky: "#ffffff",
  space: "#b67cff",
};

interface Live {
  engine: Engine;
  scene: Scene;
  canvas: HTMLCanvasElement;
  cameras: ArcRotateCamera[];
  /** Each course's tile, the tile size in device pixels, and the grid's width in tiles. */
  tiles: Map<string, number>;
  tw: number;
  th: number;
  cols: number;
  ready: boolean;
  idle: number;
  raf: number;
}

let live: Live | null = null;
const reducedMotion = typeof matchMedia === "function" ? matchMedia("(prefers-reduced-motion: reduce)") : null;

/** Are the live previews drawing? Until they are, the cards show flat outlines. */
export function previewsLive(): boolean {
  return live?.ready ?? false;
}

/** A card's live preview of course `id`: a canvas the previews draw into. */
export function previewCanvas(id: string): string {
  return `<canvas class="vd-track-3d" data-preview="${id}" width="${live?.tw ?? W}" height="${live?.th ?? H}"></canvas>`;
}

/**
 * Start the live previews, unless they're running: build every course's
 * model, and once the shaders are ready call `onReady` and, every frame, draw
 * into each `canvas[data-preview]` on screen. They stop by themselves once
 * none is showing.
 */
export function startTrackPreviews(specs: TrackSpec[], onReady: () => void): void {
  if (live) return;
  const courses = specs.slice(0, MAX_COURSES);
  const dpr = Math.min(2, Math.max(1, window.devicePixelRatio || 1));
  const tw = Math.round(W * dpr);
  const th = Math.round(H * dpr);
  const cols = Math.ceil(Math.sqrt(courses.length));
  const rows = Math.ceil(courses.length / cols);
  const canvas = document.createElement("canvas");
  canvas.width = tw * cols;
  canvas.height = th * rows;
  let engine: Engine;
  try {
    engine = new Engine(canvas, true, { preserveDrawingBuffer: true, alpha: true, premultipliedAlpha: false, antialias: true });
  } catch {
    return; // no WebGL to spare: the cards keep their flat outlines
  }
  const scene = new Scene(engine);
  scene.clearColor = new Color4(0, 0, 0, 0);
  const cameras: ArcRotateCamera[] = [];
  const tiles = new Map<string, number>();
  courses.forEach((spec, k) => {
    // Every model sits at the origin; its own camera is the only one that sees it.
    const layer = 1 << k;
    for (const m of buildMini(scene, spec)) m.layerMask = layer;
    const cam = new ArcRotateCamera(`pv-${spec.id}`, -Math.PI / 2, 1.02, 17.5, Vector3.Zero(), scene);
    cam.fov = 0.72;
    cam.layerMask = layer;
    const col = k % cols;
    const row = Math.floor(k / cols);
    cam.viewport = new Viewport(col / cols, 1 - (row + 1) / rows, 1 / cols, 1 / rows);
    cameras.push(cam);
    tiles.set(spec.id, k);
  });
  scene.activeCameras = cameras;
  const L: Live = { engine, scene, canvas, cameras, tiles, tw, th, cols, ready: false, idle: 0, raf: 0 };
  live = L;
  engine.onContextLostObservable.add(() => stopTrackPreviews());
  // Shaders compile in the background: draw only once everything's ready,
  // or the first frames come out empty.
  void scene.whenReadyAsync().then(() => {
    if (live !== L) return;
    L.ready = true;
    onReady();
    L.raf = requestAnimationFrame(frame);
  });
}

/** Let the previews' context go; they start again with the course select. */
export function stopTrackPreviews(): void {
  const L = live;
  if (!L) return;
  live = null;
  cancelAnimationFrame(L.raf);
  L.engine.dispose();
}

function frame(now: number): void {
  const L = live;
  if (!L) return;
  L.raf = requestAnimationFrame(frame);
  // Only the cards on screen: a hidden menu has none.
  const cards = [...document.querySelectorAll<HTMLCanvasElement>("canvas[data-preview]")].filter((c) => c.offsetParent !== null);
  if (!cards.length) {
    if (++L.idle > IDLE_FRAMES) stopTrackPreviews();
    return;
  }
  L.idle = 0;
  const turn = reducedMotion?.matches ? 0 : ((now / 1000) % SPIN_SECONDS) / SPIN_SECONDS;
  for (const cam of L.cameras) cam.alpha = -Math.PI / 2 + turn * Math.PI * 2;
  L.scene.render();
  for (const c of cards) {
    const k = L.tiles.get(c.dataset.preview ?? "");
    if (k === undefined) continue;
    if (c.width !== L.tw || c.height !== L.th) {
      c.width = L.tw;
      c.height = L.th;
    }
    const ctx = c.getContext("2d");
    if (!ctx) continue;
    ctx.clearRect(0, 0, L.tw, L.th);
    ctx.drawImage(L.canvas, (k % L.cols) * L.tw, Math.floor(k / L.cols) * L.th, L.tw, L.th, 0, 0, L.tw, L.th);
  }
}

/** The course as a low-poly glowing ribbon over a faint pedestal, with drop
 * lines under its high sections so the elevation reads. Fits a ~10u box. */
function buildMini(scene: Scene, spec: TrackSpec): Mesh[] {
  const accent = Color3.FromHexString(ACCENT[spec.theme ?? "neon"]);
  // The road's own frames, so walls, loops and corkscrews read in the model too.
  const fr = roadFrames(spec, 8);
  const pts = [...fr.centers, fr.centers[0]];
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const p of pts) {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minZ = Math.min(minZ, p.z);
    maxZ = Math.max(maxZ, p.z);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  }
  const s = 10 / Math.max(maxX - minX, maxZ - minZ, 1);
  const cx = (minX + maxX) / 2;
  const cz = (minZ + maxZ) / 2;
  const base = -1.2;
  // Exaggerate height so a course's shape reads, but keep tall ones in frame —
  // except where the road loops or rolls, which only reads at true proportions.
  const loops = spec.ups?.some((u) => u) ?? false;
  const ys = loops ? s : s * Math.min(2.2, 4.2 / Math.max(1e-6, (maxY - minY) * s));
  const toMini = (p: Vector3) => new Vector3((p.x - cx) * s, base + 0.35 + (p.y - minY) * ys, (p.z - cz) * s);
  const P = pts.map(toMini);
  // Edges across the road's own surface (in world units, then into the model).
  const hwWorld = Math.max(0.16 / s, spec.roadHalfWidth * 1.5);
  const hw = hwWorld * s;
  const n = P.length - 1; // the loop repeats its first point at the end
  let pathArray: Vector3[][];
  if (!fr.wrap.some((w) => w !== 0)) {
    const left: Vector3[] = [];
    const right: Vector3[] = [];
    for (let i = 0; i <= n; i++) {
      const c = fr.centers[i % n];
      const a = fr.across[i % n];
      left.push(toMini(c.subtract(a.scale(hwWorld))));
      right.push(toMini(c.add(a.scale(hwWorld))));
    }
    pathArray = [left, right];
  } else {
    // Pipes and cylinders: the road's own curve, widened like the flat road.
    const m = hwWorld / spec.roadHalfWidth;
    pathArray = [];
    for (let k = 0; k <= MINI_STRIPS; k++) pathArray.push([]);
    for (let i = 0; i <= n; i++) {
      const j = i % n;
      const hw = fr.halfWidth[j];
      const kappa = curlOf(fr.wrap[j], hw);
      for (let k = 0; k <= MINI_STRIPS; k++) {
        const u = -hw + (2 * hw * k) / MINI_STRIPS;
        pathArray[k].push(toMini(fr.centers[j].add(crossOffset(fr.up[j], fr.across[j], kappa, u).scaleInPlace(m))));
      }
    }
  }
  const left = pathArray[0];
  const right = pathArray[pathArray.length - 1];
  const out: Mesh[] = [];
  const ribbon = MeshBuilder.CreateRibbon("pvRoad", { pathArray, sideOrientation: Mesh.DOUBLESIDE }, scene);
  const rm = new StandardMaterial("pvRoadMat", scene);
  rm.disableLighting = true;
  rm.emissiveColor = accent.scale(0.55);
  ribbon.material = rm;
  out.push(ribbon);

  for (const edge of [left, right]) {
    const line = MeshBuilder.CreateLines("pvEdge", { points: edge }, scene);
    line.color = accent;
    out.push(line);
  }
  const drops: Vector3[][] = [];
  for (let i = 0; i < n; i += 6) {
    const c = P[i];
    if (c.y - base > 0.9) drops.push([c, new Vector3(c.x, base, c.z)]);
  }
  if (drops.length) {
    const ls = MeshBuilder.CreateLineSystem("pvDrops", { lines: drops }, scene);
    ls.color = accent.scale(0.45);
    out.push(ls);
  }
  const disc = MeshBuilder.CreateDisc("pvBase", { radius: 7.2, tessellation: 48 }, scene);
  disc.rotation.x = Math.PI / 2;
  disc.position.y = base;
  const dm = new StandardMaterial("pvBaseMat", scene);
  dm.disableLighting = true;
  dm.emissiveColor = accent.scale(0.35);
  dm.alpha = 0.22;
  disc.material = dm;
  out.push(disc);
  const ring = MeshBuilder.CreateTorus("pvRing", { diameter: 14.4, thickness: 0.08, tessellation: 64 }, scene);
  ring.position.y = base;
  const gm = new StandardMaterial("pvRingMat", scene);
  gm.disableLighting = true;
  gm.emissiveColor = accent;
  ring.material = gm;
  out.push(ring);
  // start line marker
  const start = MeshBuilder.CreateBox("pvStart", { width: hw * 2.6, height: 0.12, depth: 0.22 }, scene);
  const p0 = P[0];
  const p1 = P[1];
  start.position.copyFrom(p0);
  start.rotation.y = Math.atan2(p1.x - p0.x, p1.z - p0.z);
  const sm = new StandardMaterial("pvStartMat", scene);
  sm.disableLighting = true;
  sm.emissiveColor = new Color3(1, 1, 1);
  start.material = sm;
  out.push(start);
  return out;
}
